import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const LINKS = [
  { id: "services", label: "Services" },
  { id: "process", label: "Process" },
  { id: "work", label: "Work" },
  { id: "pricing", label: "Pricing" },
  { id: "faq", label: "FAQ" },
];

export const scrollToId = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="navbar-header"
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled ? "glass-panel border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <button data-testid="nav-logo-btn" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Autoric home">
            <Logo compact={!scrolled} />
          </button>
          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-gray-400 hover:text-sky-400 transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <a
              data-testid="nav-call-link"
              href="tel:0415414296"
              className="hidden sm:flex items-center gap-2 font-mono text-xs tracking-widest text-gray-300 hover:text-sky-400 transition-colors"
            >
              <Phone size={14} className="text-sky-500" />
              0415 414 296
            </a>
            <button
              data-testid="nav-enquire-btn"
              onClick={() => go("contact")}
              className="hidden sm:inline-flex bg-sky-500 text-obsidian font-display font-bold uppercase tracking-widest text-sm px-5 py-2.5 rounded-md hover:bg-sky-400 hover:shadow-[0_0_20px_rgba(14,165,233,0.4)] transition-[background-color,box-shadow] duration-300"
            >
              Enquire Now
            </button>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              className="lg:hidden text-gray-200 p-2"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-obsidian/95 backdrop-blur-xl flex flex-col items-center justify-center gap-6 lg:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                data-testid={`mobile-nav-link-${l.id}`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * i, duration: 0.4 }}
                onClick={() => go(l.id)}
                className="font-display text-4xl font-bold uppercase tracking-wide text-gray-200 hover:text-sky-400 transition-colors"
              >
                {l.label}
              </motion.button>
            ))}
            <motion.a
              data-testid="mobile-nav-call"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              href="tel:0415414296"
              className="mt-4 flex items-center gap-3 font-mono text-sm tracking-widest text-sky-400"
            >
              <Phone size={16} /> 0415 414 296
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
